"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "DataV3Service", {
    enumerable: true,
    get: function() {
        return DataV3Service;
    }
});
const _nocodbsdk = require("nocodb-sdk");
const _common = require("@nestjs/common");
const _ltarcolsupdater = require("src/db/BaseModelSqlv2/ltar-cols-updater");
const _dbHelpers = require("~/helpers/dbHelpers");
const _catchError = require("~/helpers/catchError");
const _models = require("~/models");
const _PagedResponse = require("~/helpers/PagedResponse");
const _datatableservice = require("~/services/data-table.service");
const _BaseModelSqlv2 = require("~/db/BaseModelSqlv2");
const _NcConnectionMgrv2 = /*#__PURE__*/ _interop_require_default(require("~/utils/common/NcConnectionMgrv2"));
const _constants = require("~/constants");
const _utils = require("~/utils");
const _profiler = require("~/helpers/profiler");
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let DataV3Service = class DataV3Service {
    /**
   * Get model information including primary key and columns
   */ async getModelInfo(context, modelId, user) {
        const { model } = await this.dataTableService.getModelAndView(context, {
            modelId,
            user
        });
        const columns = await model.getColumns(context);
        const primaryKey = model.primaryKey;
        const primaryKeys = model.primaryKeys;
        return {
            model,
            primaryKey,
            primaryKeys,
            columns
        };
    }
    /**
   * Get related model information for LTAR columns
   */ async getRelatedModelInfo(context, column) {
        const { refContext } = column.colOptions.getRelContext(context);
        const relatedModel = await column.colOptions.getRelatedTable(refContext);
        await relatedModel.getColumns(refContext);
        const relatedPrimaryKey = relatedModel.primaryKey;
        const primaryKeys = relatedModel.primaryKeys;
        return {
            model: relatedModel,
            primaryKey: relatedPrimaryKey,
            primaryKeys
        };
    }
    /**
   * Extract requested fields from query parameters
   */ getRequestedFields(query) {
        const fields = (query == null ? void 0 : query.fields) || (query == null ? void 0 : query.f);
        if (!fields) return undefined;
        if (Array.isArray(fields)) {
            return fields;
        }
        if (typeof fields === 'string') {
            return fields.split(',').map((f)=>f.trim());
        }
        return undefined;
    }
    /**
   * Transform a record to the v3 format {id, fields}
   */ async transformRecordToV3Format(param) {
        const { context, record, primaryKey, primaryKeys, requestedFields, columns, nestedLimit, skipSubstitutingColumnIds, reuse = {}, depth = 0, linksAsLtar = false } = param;
        const getPrimaryKey = (column)=>{
            return skipSubstitutingColumnIds ? column.id : column.title;
        };
        // If specific fields were requested, only include those in the fields object
        // Otherwise, include all non-primary-key fields
        const primaryKeyTitles = primaryKeys ? primaryKeys.map((pk)=>getPrimaryKey(pk)) : [
            getPrimaryKey(primaryKey)
        ];
        const shouldIncludeField = (key)=>{
            // For APIv3, primary keys should NEVER be in the fields object
            // They are always returned as the 'id' property at the root level
            if (primaryKeyTitles.includes(key)) {
                return false;
            }
            // For non-primary-key fields, include them if:
            // 1. No field selection was made, OR
            // 2. Field selection was made and this field is in the selection
            if (!requestedFields) {
                return true; // No field selection, include all non-PK fields
            }
            if (requestedFields.includes(key)) {
                return true;
            }
            const foundColumn = columns.find((c)=>c.title === key || c.id === key);
            if (foundColumn) {
                return requestedFields.includes(foundColumn.id) || requestedFields.includes(foundColumn.title);
            }
        };
        const fields = _extends({}, record);
        const transformedFields = {};
        // Process each field
        for (const [key, value] of Object.entries(fields)){
            if (!shouldIncludeField(key)) continue;
            // Handle LTAR fields if columns are provided
            if (columns) {
                const column = columns.find((col)=>col.title === key);
                if ((column == null ? void 0 : column.uidt) === _nocodbsdk.UITypes.LinkToAnotherRecord || (column == null ? void 0 : column.uidt) === _nocodbsdk.UITypes.Links && linksAsLtar) {
                    if (Array.isArray(value)) {
                        // Check depth limit to prevent unbounded recursion
                        if (depth >= _constants.MAX_NESTING_DEPTH) {
                            // At max depth, return simplified representation based on relation type
                            transformedFields[key] = value.map((nestedRecord)=>{
                                if (typeof nestedRecord === 'object' && nestedRecord !== null) {
                                    // Try to extract ID from the nested record with fallbacks
                                    const id = nestedRecord.id || nestedRecord.Id || nestedRecord.ID || Object.values(nestedRecord)[0];
                                    // For read operations, handle missing IDs gracefully
                                    return {
                                        id: id ? String(id) : null
                                    };
                                }
                                // Handle primitive values - for read operations, convert to string
                                return {
                                    id: nestedRecord ? String(nestedRecord) : null
                                };
                            });
                            continue;
                        }
                        // Cache the related model info per column to avoid N+1 queries
                        const relatedModelInfo = await (0, _utils.reuseOrSave)(`relatedModelInfo_${column.id}`, reuse, async ()=>this.getRelatedModelInfo(context, column));
                        const { primaryKey: relatedPrimaryKey, primaryKeys: relatedPrimaryKeys } = relatedModelInfo;
                        // Handle array of linked records with concurrency control
                        const recordsToProcess = nestedLimit && value.length > nestedLimit ? value.slice(0, nestedLimit) : value;
                        // Transform all nested records using v3 format
                        transformedFields[key] = await (0, _utils.processConcurrently)(recordsToProcess, async (nestedRecord)=>{
                            return this.transformRecordToV3Format({
                                context: context,
                                record: nestedRecord,
                                primaryKey: relatedPrimaryKey,
                                primaryKeys: relatedPrimaryKeys,
                                columns: relatedModelInfo.model.columns,
                                reuse,
                                depth: depth + 1
                            });
                        });
                        continue;
                    } else if (value && typeof value === 'object') {
                        // Handle single nested record (typically BELONGS_TO or ONE_TO_ONE)
                        const relatedModelInfo = await (0, _utils.reuseOrSave)(`relatedModelInfo_${column.id}`, reuse, async ()=>this.getRelatedModelInfo(context, column));
                        const { primaryKey: relatedPrimaryKey, primaryKeys: relatedPrimaryKeys } = relatedModelInfo;
                        // Transform single record using v3 format
                        transformedFields[key] = await this.transformRecordToV3Format({
                            context: context,
                            record: value,
                            primaryKey: relatedPrimaryKey,
                            primaryKeys: relatedPrimaryKeys,
                            columns: relatedModelInfo.model.columns,
                            reuse,
                            depth: depth + 1
                        });
                        continue;
                    }
                }
            }
            // For non-LTAR fields, just copy the value
            transformedFields[key] = value;
        }
        const recordPrimaryKeyValue = primaryKeys ? (0, _dbHelpers.getCompositePkValue)(primaryKeys, record, {
            skipSubstitutingColumnIds
        }) : record[getPrimaryKey(primaryKey)];
        const result = {
            // Always include the 'id' property for APIv3
            id: recordPrimaryKeyValue,
            fields: transformedFields
        };
        return result;
    }
    /**
   * Transform multiple records to v3 format
   */ async transformRecordsToV3Format(param) {
        const { records } = param;
        // Use concurrency control to prevent overwhelming the system
        return (0, _utils.processConcurrently)(records, async (record)=>this.transformRecordToV3Format(_extends({}, param, {
                record
            })));
    }
    async validateDataListQueryParams(context, param) {
        const columns = param.modelInfo.columns;
        if (param.query.sort) {
            let fieldsArr = [];
            let parsedSortJSON;
            if (typeof param.query.sort === 'string') {
                try {
                    parsedSortJSON = JSON.parse(param.query.sort);
                } catch (unused) {
                    _catchError.NcError.get(context).invalidRequestBody(`Query parameter 'sort' needs to a JSON string in format of [{"field": "fieldId", "direction": "asc"}]`);
                }
            } else if (typeof param.query.sort === 'object') {
                parsedSortJSON = param.query.sort;
            } else {
                _catchError.NcError.get(context).invalidRequestBody(`Query parameter 'sort' needs to be a single string`);
            }
            const parsedSort = Array.isArray(parsedSortJSON) ? parsedSortJSON : [
                parsedSortJSON
            ];
            if (parsedSort.some((s)=>s.direction && ![
                    'asc',
                    'desc'
                ].includes(s.direction))) {
                _catchError.NcError.get(context).invalidRequestBody(`Query parameter 'sort' direction value can only be 'asc' or 'desc'`);
            }
            fieldsArr = parsedSort.map((s)=>s.field);
            const idList = columns.map((col)=>col.id);
            const titleList = columns.map((col)=>col.title);
            const columnNameList = columns.map((col)=>col.column_name);
            const hayStack = [
                ...idList,
                ...titleList,
                ...columnNameList
            ];
            const notFoundField = fieldsArr.find((field)=>!hayStack.includes(field));
            if (notFoundField) {
                _catchError.NcError.get(context).fieldNotFound({
                    field: notFoundField,
                    onSection: `'sort' query parameter`
                });
            }
        }
        if (param.query.fields) {
            let fieldsArr = [];
            if (typeof param.query.fields !== 'string') {
                if (Array.isArray(param.query.fields)) {
                    fieldsArr = param.query.fields;
                } else {
                    _catchError.NcError.get(context).invalidRequestBody(`Query parameter 'fields' needs to be a single string`);
                }
            } else if (param.query.fields.startsWith('[')) {
                try {
                    fieldsArr = JSON.parse(param.query.fields);
                    param.query.fields = fieldsArr;
                } catch (unused) {
                    _catchError.NcError.get(context).invalidRequestBody(`Query parameter fields need to be an array of string, or a comma separated`);
                }
            } else {
                fieldsArr = param.query.fields.split(',');
            }
            const idList = columns.map((col)=>col.id);
            const titleList = columns.map((col)=>col.title);
            const columnNameList = columns.map((col)=>col.column_name);
            const hayStack = [
                ...idList,
                ...titleList,
                ...columnNameList
            ];
            const notFoundField = fieldsArr.find((field)=>!hayStack.includes(field));
            if (notFoundField) {
                _catchError.NcError.get(context).fieldNotFound({
                    field: notFoundField,
                    onSection: `'fields' query parameter`
                });
            }
        }
    }
    async dataList(context, param, pagination = true) {
        var _param_query, _param_query1, _param_query2, _param_query3;
        const modelInfo = await this.getModelInfo(context, param.modelId);
        const { primaryKey, primaryKeys, columns } = modelInfo;
        await this.validateDataListQueryParams(context, _extends({}, param, {
            modelInfo
        }));
        const pagedData = await this.dataTableService.dataList(context, _extends({}, param, {
            query: _extends({}, param.query, {
                limit: +((_param_query = param.query) == null ? void 0 : _param_query.limit) || +((_param_query1 = param.query) == null ? void 0 : _param_query1.pageSize)
            }),
            apiVersion: _nocodbsdk.NcApiVersion.V3
        }));
        // Extract requested fields from query parameters
        const requestedFields = this.getRequestedFields(param.query);
        // Extract nested page limit
        const nestedLimit = +((_param_query2 = param.query) == null ? void 0 : _param_query2.nestedLimit) || _BaseModelSqlv2.BaseModelSqlv2.config.ltarV3Limit;
        const nestedPage = Math.max(+((_param_query3 = param.query) == null ? void 0 : _param_query3.nestedPage) || 1, 1);
        const nestedPrevPageAvail = nestedPage > 1;
        const pagedResponse = new _PagedResponse.PagedResponseV3Impl(pagedData, {
            context,
            tableId: param.modelId,
            baseUrl: param.req.ncSiteUrl,
            nestedNextPageAvail: false,
            nestedPrevPageAvail,
            queryParams: param.query
        });
        const linksAsLtar = param.query[_constants.QUERY_STRING_LINKS_AS_LTAR] === 'true';
        // Transform records with LTAR handling
        const transformedRecords = await this.transformRecordsToV3Format({
            context: context,
            records: pagedResponse.list,
            primaryKey: primaryKey,
            primaryKeys: primaryKeys,
            requestedFields: requestedFields,
            columns: columns,
            nestedLimit: nestedLimit,
            skipSubstitutingColumnIds: param.query[_constants.QUERY_STRING_FIELD_ID_ON_RESULT] === 'true',
            reuse: {},
            depth: 0,
            linksAsLtar
        });
        if (!pagination) {
            return transformedRecords;
        }
        // Check if any LTAR fields were truncated
        const hasNextPage = transformedRecords.some((record)=>Object.values(record.fields).some((value)=>Array.isArray(value) && value.length === nestedLimit));
        return {
            records: transformedRecords,
            next: pagedResponse.pageInfo.next,
            prev: pagedResponse.pageInfo.prev,
            nestedNext: hasNextPage ? pagedResponse.pageInfo.nestedNext : null,
            nestedPrev: pagedResponse.pageInfo.nestedPrev
        };
    }
    /**
   * Transform LTAR fields from v3 format to internal format
   */ async transformLTARFieldsToInternal(context, fields, ltarColumns, option) {
        if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
            return fields != null ? fields : {};
        }
        const transformedFields = _extends({}, fields);
        const getPrimaryKey = (column)=>{
            return (option == null ? void 0 : option.skipSubstitutingColumnIds) ? column.id : column.title;
        };
        for (const column of ltarColumns){
            const key = (0, _dbHelpers.dataWrapper)(fields).getColumnKeyName(column);
            if (fields[key]) {
                const { primaryKey: relatedPrimaryKey, primaryKeys: relatedPrimaryKeys } = await this.getRelatedModelInfo(context, column);
                const fieldValue = fields[key];
                // Handle v3 format consistently for all relation types
                if (Array.isArray(fieldValue)) {
                    // Array of records - each should have id property
                    transformedFields[key] = fieldValue.map((nestedRecord)=>this.convertRecordIdToInternal(context, nestedRecord, relatedPrimaryKey, relatedPrimaryKeys, getPrimaryKey));
                } else if (fieldValue && typeof fieldValue === 'object' && fieldValue.id) {
                    // Single record with id property (v3 format)
                    transformedFields[key] = this.convertRecordIdToInternal(context, fieldValue, relatedPrimaryKey, relatedPrimaryKeys, getPrimaryKey);
                } else if (fieldValue === null) {
                    transformedFields[key] = null;
                }
            }
        }
        return transformedFields;
    }
    /**
   * Convert a record ID from v3 format to internal format
   */ convertRecordIdToInternal(context, nestedRecord, relatedPrimaryKey, relatedPrimaryKeys, getPrimaryKey) {
        // For composite PKs, split the id and create the appropriate object
        if (relatedPrimaryKeys.length > 1) {
            const idString = String(nestedRecord.id);
            const idParts = idString.split('___');
            // Validate that we have the correct number of parts
            if (idParts.length !== relatedPrimaryKeys.length) {
                _catchError.NcError.get(context).unprocessableEntity(`Invalid composite key: expected ${relatedPrimaryKeys.length} parts but got ${idParts.length} in "${idString}"`);
            }
            const pkObject = {};
            relatedPrimaryKeys.forEach((pk, index)=>{
                const part = idParts[index];
                // Validate that the part exists and is not empty
                if (part === undefined || part === null) {
                    _catchError.NcError.get(context).unprocessableEntity(`Invalid composite key part at index ${index}: got ${part} in "${idString}"`);
                }
                // Handle escaped underscores, but validate the result
                const cleanedPart = part.replaceAll('\\_', '_');
                // Don't allow completely empty string primary keys (after cleaning)
                if (cleanedPart === '') {
                    _catchError.NcError.get(context).unprocessableEntity(`Empty primary key part at index ${index} after cleaning in "${idString}"`);
                }
                pkObject[getPrimaryKey(pk)] = cleanedPart;
            });
            return pkObject;
        } else {
            // Single primary key - validate it's not empty
            const pkValue = String(nestedRecord.id);
            if (pkValue === '' || pkValue === 'undefined' || pkValue === 'null') {
                _catchError.NcError.get(context).unprocessableEntity(`Invalid primary key value: "${pkValue}"`);
            }
            return {
                [getPrimaryKey(relatedPrimaryKey)]: pkValue
            };
        }
    }
    async dataInsert(context, param) {
        var _result_primaryKey_id;
        var _param_cookie_query, _param_cookie_query1;
        // validate insert
        this.validateRequestFormat(context, {
            body: param.body,
            validateAdditionalProp: true
        });
        const { model, primaryKey, primaryKeys, columns } = await this.getModelInfo(context, param.modelId);
        const ltarColumns = columns.filter((col)=>(0, _nocodbsdk.isLinksOrLTAR)(col));
        // Transform the request body to match internal format
        const transformedBody = Array.isArray(param.body) ? await Promise.all(param.body.map(async (record)=>this.transformLTARFieldsToInternal(context, record.fields, ltarColumns))) : [
            await this.transformLTARFieldsToInternal(context, param.body.fields, ltarColumns)
        ];
        if (transformedBody.length > _constants.V3_DATA_PAYLOAD_LIMIT) {
            _catchError.NcError.get(context).maxPayloadLimitExceeded(_constants.V3_DATA_PAYLOAD_LIMIT);
        }
        const result = await this.dataTableService.dataInsert(context, _extends({}, param, {
            body: transformedBody,
            apiVersion: _nocodbsdk.NcApiVersion.V3
        }));
        // Transform the response to match the new format
        if (!result || typeof result !== 'object') {
            return {
                records: []
            };
        }
        const hasPrimaryKey = (obj)=>{
            return primaryKey.id in obj || primaryKey.title in obj;
        };
        // Extract inserted record IDs
        const insertedIds = Array.isArray(result) ? result.map((record)=>{
            var _record_primaryKey_id;
            return (_record_primaryKey_id = record[primaryKey.id]) != null ? _record_primaryKey_id : record[primaryKey.title];
        }).filter((id)=>id != null) : hasPrimaryKey(result) ? [
            (_result_primaryKey_id = result[primaryKey.id]) != null ? _result_primaryKey_id : result[primaryKey.title]
        ] : [];
        if (insertedIds.length === 0) {
            return {
                records: []
            };
        }
        // Fetch full records using baseModel.chunkList() for better performance
        const source = await _models.Source.get(context, model.source_id);
        const baseModel = await _models.Model.getBaseModelSQL(context, {
            id: model.id,
            dbDriver: await _NcConnectionMgrv2.default.get(source),
            source
        });
        // Convert IDs to strings for chunkList
        const idsAsStrings = insertedIds.map((id)=>String(id));
        const linksAsLtar = ((_param_cookie_query = param.cookie.query) == null ? void 0 : _param_cookie_query[_constants.QUERY_STRING_LINKS_AS_LTAR]) === 'true';
        // Fetch all records in bulk
        const fullRecords = await baseModel.chunkList({
            pks: idsAsStrings,
            apiVersion: _nocodbsdk.NcApiVersion.V3,
            args: _extends({}, linksAsLtar ? {
                linksAsLtar: 'true'
            } : {})
        });
        // Create a map for quick lookup by ID
        const recordMap = new Map();
        for (const record of fullRecords){
            const recordId = baseModel.extractPksValues(record, true);
            recordMap.set(String(recordId), record);
        }
        // Maintain the original order of insertedIds
        const orderedRecords = [];
        for (const id of insertedIds){
            const record = recordMap.get(String(id));
            if (record) {
                orderedRecords.push(record);
            }
        }
        // Transform and return full records in V3 format
        return {
            records: await this.transformRecordsToV3Format({
                context: context,
                records: orderedRecords,
                primaryKey: primaryKey,
                primaryKeys: primaryKeys,
                requestedFields: undefined,
                columns: columns,
                nestedLimit: undefined,
                skipSubstitutingColumnIds: ((_param_cookie_query1 = param.cookie.query) == null ? void 0 : _param_cookie_query1[_constants.QUERY_STRING_FIELD_ID_ON_RESULT]) === 'true',
                reuse: {},
                depth: 0,
                linksAsLtar
            })
        };
    }
    async dataDelete(context, param) {
        // validate update
        this.validateRequestFormat(context, {
            body: param.body,
            validateId: true
        });
        // Merge the request body with the records in query params
        param.body = [
            ...Array.isArray(param.body) ? param.body : param.body ? [
                param.body
            ] : [],
            ...param.queryRecords ? Array.isArray(param.queryRecords) ? param.queryRecords.map((id)=>({
                    id
                })) : [
                {
                    id: param.queryRecords
                }
            ] : []
        ];
        const { primaryKey } = await this.getModelInfo(context, param.modelId);
        // Transform the request body to match internal format
        const recordIds = param.body.map((record)=>({
                [primaryKey.title]: record.id
            }));
        if (recordIds.length > _constants.V3_DATA_PAYLOAD_LIMIT) {
            _catchError.NcError.get(context).maxPayloadLimitExceeded(_constants.V3_DATA_PAYLOAD_LIMIT);
        }
        await this.dataTableService.dataDelete(context, _extends({}, param, {
            body: recordIds
        }));
        // Transform the response to match the new format
        return {
            records: param.body.map((record)=>({
                    id: record.id,
                    deleted: true
                }))
        };
    }
    async dataUpdate(context, param) {
        var _param_cookie_query, _param_cookie_query1;
        // validate update
        this.validateRequestFormat(context, {
            body: param.body,
            validateAdditionalProp: true,
            validateId: true
        });
        const profiler = _profiler.Profiler.start(`data-v3/dataUpdate`);
        const { model, primaryKey, primaryKeys, columns } = await this.getModelInfo(context, param.modelId);
        profiler.log(`getModelInfo done`);
        const ltarColumns = columns.filter((col)=>(0, _nocodbsdk.isLinksOrLTAR)(col));
        // Transform the request body to match internal format
        const transformedBody = Array.isArray(param.body) ? await Promise.all(param.body.map(async (record)=>_extends({
                [primaryKey.title]: record.id
            }, await this.transformLTARFieldsToInternal(context, record.fields, ltarColumns)))) : [
            _extends({
                [primaryKey.title]: param.body.id
            }, await this.transformLTARFieldsToInternal(context, param.body.fields, ltarColumns))
        ];
        profiler.log(`transformLTARFieldsToInternal done`);
        if (transformedBody.length > _constants.V3_DATA_PAYLOAD_LIMIT) {
            _catchError.NcError.get(context).maxPayloadLimitExceeded(_constants.V3_DATA_PAYLOAD_LIMIT);
        }
        await this.dataTableService.dataUpdate(context, _extends({}, param, {
            body: transformedBody,
            apiVersion: _nocodbsdk.NcApiVersion.V3
        }));
        profiler.log(`dataTableService.dataUpdate done`);
        // Extract updated record IDs
        const updatedIds = Array.isArray(param.body) ? param.body.map((record)=>record.id) : [
            param.body.id
        ];
        if (updatedIds.length === 0) {
            profiler.end();
            return {
                records: []
            };
        }
        // Fetch full records using baseModel.chunkList() for better performance
        const source = await _models.Source.get(context, model.source_id);
        const baseModel = await _models.Model.getBaseModelSQL(context, {
            id: model.id,
            dbDriver: await _NcConnectionMgrv2.default.get(source),
            source
        });
        // Convert IDs to strings for chunkList
        const idsAsStrings = updatedIds.map((id)=>String(id));
        const linksAsLtar = ((_param_cookie_query = param.cookie.query) == null ? void 0 : _param_cookie_query[_constants.QUERY_STRING_LINKS_AS_LTAR]) === 'true';
        // Fetch all records in bulk
        const fullRecords = await baseModel.chunkList({
            pks: idsAsStrings,
            apiVersion: context.api_version,
            args: _extends({}, linksAsLtar ? {
                linksAsLtar: 'true'
            } : {})
        });
        profiler.log(`baseModel.chunkList done`);
        // Create a map for quick lookup by ID
        const recordMap = new Map();
        for (const record of fullRecords){
            const recordId = baseModel.extractPksValues(record, true);
            recordMap.set(String(recordId), record);
        }
        // Maintain the original order of updatedIds
        const orderedRecords = [];
        for (const id of updatedIds){
            const record = recordMap.get(String(id));
            if (record) {
                orderedRecords.push(record);
            }
        }
        const resultRecords = await this.transformRecordsToV3Format({
            context: context,
            records: orderedRecords,
            primaryKey: primaryKey,
            primaryKeys: primaryKeys,
            requestedFields: undefined,
            columns: columns,
            nestedLimit: undefined,
            skipSubstitutingColumnIds: (_param_cookie_query1 = param.cookie.query) == null ? void 0 : _param_cookie_query1[_constants.QUERY_STRING_FIELD_ID_ON_RESULT],
            reuse: {},
            depth: 0,
            linksAsLtar
        });
        profiler.end();
        // Transform and return full records in V3 format
        return {
            records: resultRecords
        };
    }
    validateRequestFormat(context, param) {
        for (const [index, row] of (Array.isArray(param.body) ? param.body : [
            param.body
        ]).entries()){
            if (param.validateId) {
                if (!row.id) {
                    _catchError.NcError.get(context).invalidRequestBody(`Property 'id' is required on index ${index}`);
                }
            }
            if (param.validateAdditionalProp) {
                const otherProps = Object.keys(row).filter((prop)=>![
                        'id',
                        'fields'
                    ].includes(prop));
                if (otherProps.length) {
                    _catchError.NcError.get(context).invalidRequestBody(`Properties ${otherProps.map((field)=>`'${field}'`).join(',')} on index ${index} is not allowed. All record parameters need to be put inside 'fields' property`);
                }
            }
        }
    }
    async nestedDataList(context, param) {
        var _param_query, _param_query1, _param_query2, _param_query3, _param_query4;
        const response = await this.dataTableService.nestedDataList(context, _extends({}, param, {
            query: _extends({}, param.query, {
                limit: +((_param_query = param.query) == null ? void 0 : _param_query.limit) || +((_param_query1 = param.query) == null ? void 0 : _param_query1.pageSize)
            }),
            apiVersion: _nocodbsdk.NcApiVersion.V3
        }));
        const column = await _models.Column.get(context, {
            colId: param.columnId
        });
        const { primaryKey: relatedPrimaryKey, primaryKeys: relatedPrimaryKeys } = await this.getRelatedModelInfo(context, column);
        const colOptions = await column.getColOptions(context);
        const isSingleRelation = colOptions.type === _nocodbsdk.RelationTypes.BELONGS_TO || colOptions.type === _nocodbsdk.RelationTypes.ONE_TO_ONE;
        // Handle case where response is a single object (ONE_TO_ONE, BELONGS_TO)
        if (response && typeof response === 'object' && (relatedPrimaryKey.title in response || relatedPrimaryKey.id in response)) {
            var _param_query5, _param_query6;
            // Extract requested fields from query parameters for nested data
            const requestedFields = this.getRequestedFields(param.query);
            // Get related model columns for LTAR transformation
            const relatedModel = await colOptions.getRelatedTable(context);
            const relatedColumns = await relatedModel.getColumns(context);
            const linksAsLtar = ((_param_query5 = param.query) == null ? void 0 : _param_query5[_constants.QUERY_STRING_LINKS_AS_LTAR]) === 'true';
            const transformedRecord = await this.transformRecordToV3Format({
                context: context,
                record: response,
                primaryKey: relatedPrimaryKey,
                primaryKeys: relatedPrimaryKeys,
                requestedFields: requestedFields,
                columns: relatedColumns,
                nestedLimit: undefined,
                skipSubstitutingColumnIds: ((_param_query6 = param.query) == null ? void 0 : _param_query6[_constants.QUERY_STRING_FIELD_ID_ON_RESULT]) === 'true',
                reuse: {},
                depth: 0,
                linksAsLtar
            });
            // For single relations, return the record directly, for others return as array
            return isSingleRelation ? {
                record: transformedRecord,
                next: null,
                prev: null,
                nestedNext: null,
                nestedPrev: null
            } : {
                records: [
                    transformedRecord
                ],
                next: null,
                prev: null,
                nestedNext: null,
                nestedPrev: null
            };
        }
        // Handle case where response is a paginated list (HAS_MANY, MANY_TO_MANY)
        if (!response || !('list' in response) || !('pageInfo' in response)) {
            // For single relations, return null record, for others return empty array
            return isSingleRelation ? {
                record: null,
                next: null,
                prev: null,
                nestedNext: null,
                nestedPrev: null
            } : {
                records: [],
                next: null,
                prev: null,
                nestedNext: null,
                nestedPrev: null
            };
        }
        const pagedResponse = new _PagedResponse.PagedResponseV3Impl(response, {
            context,
            tableId: param.modelId,
            baseUrl: param.req.ncSiteUrl,
            queryParams: param.query
        });
        // Extract requested fields from query parameters for nested data
        const requestedFields = this.getRequestedFields(param.query);
        // Get related model columns for LTAR transformation
        const relatedModel = await colOptions.getRelatedTable(context);
        const relatedColumns = await relatedModel.getColumns(context);
        // Extract nested page limit
        const nestedLimit = +((_param_query2 = param.query) == null ? void 0 : _param_query2.nestedLimit) || _BaseModelSqlv2.BaseModelSqlv2.config.ltarV3Limit;
        const linksAsLtar = ((_param_query3 = param.query) == null ? void 0 : _param_query3[_constants.QUERY_STRING_LINKS_AS_LTAR]) === 'true';
        const transformedRecords = await this.transformRecordsToV3Format({
            context: context,
            records: pagedResponse.list,
            primaryKey: relatedPrimaryKey,
            primaryKeys: relatedPrimaryKeys,
            requestedFields: requestedFields,
            columns: relatedColumns,
            nestedLimit: nestedLimit,
            skipSubstitutingColumnIds: ((_param_query4 = param.query) == null ? void 0 : _param_query4[_constants.QUERY_STRING_FIELD_ID_ON_RESULT]) === 'true',
            reuse: {},
            depth: 0,
            linksAsLtar
        });
        // Check if any LTAR fields were truncated
        const hasNextPage = transformedRecords.some((record)=>Object.values(record.fields).some((value)=>Array.isArray(value) && value.length === nestedLimit));
        // For single relations, return the record directly, for others return as array
        if (isSingleRelation) {
            const singleResponse = {
                record: transformedRecords[0] || null,
                next: pagedResponse.pageInfo.next,
                prev: pagedResponse.pageInfo.prev,
                nestedPrev: pagedResponse.pageInfo.nestedPrev
            };
            if (hasNextPage) {
                singleResponse.nestedNext = pagedResponse.pageInfo.nestedNext;
            }
            return singleResponse;
        }
        const listResponse = {
            records: transformedRecords,
            next: pagedResponse.pageInfo.next,
            prev: pagedResponse.pageInfo.prev,
            nestedPrev: pagedResponse.pageInfo.nestedPrev
        };
        if (hasNextPage) {
            listResponse.nestedNext = pagedResponse.pageInfo.nestedNext;
        }
        return listResponse;
    }
    async dataRead(context, param) {
        var _param_query;
        const { primaryKey, primaryKeys, columns } = await this.getModelInfo(context, param.modelId);
        // Extract requested fields from query parameters
        const requestedFields = this.getRequestedFields(param.query);
        const result = await this.dataTableService.dataRead(context, _extends({}, param, {
            apiVersion: _nocodbsdk.NcApiVersion.V3
        }));
        // Transform the response to match the new format
        if (!result || typeof result !== 'object') {
            return {
                id: '',
                fields: {}
            };
        }
        const hasPrimaryKey = (obj)=>{
            return primaryKey.title in obj || primaryKey.id in obj;
        };
        const linksAsLtar = param.query[_constants.QUERY_STRING_LINKS_AS_LTAR] === 'true';
        return hasPrimaryKey(result) ? await this.transformRecordToV3Format({
            context: context,
            record: result,
            primaryKey: primaryKey,
            primaryKeys: primaryKeys,
            requestedFields: requestedFields,
            columns: columns,
            nestedLimit: undefined,
            skipSubstitutingColumnIds: ((_param_query = param.query) == null ? void 0 : _param_query[_constants.QUERY_STRING_FIELD_ID_ON_RESULT]) === 'true',
            reuse: {},
            depth: 0,
            linksAsLtar
        }) : {
            id: '',
            fields: {}
        };
    }
    /**
   * Normalize various refRowIds formats to string[]
   */ normalizeRefRowIds(refRowIds) {
        // Handle array of objects with id property (lowercase - APIv3 format)
        if (Array.isArray(refRowIds) && refRowIds.length > 0 && typeof refRowIds[0] === 'object' && 'id' in refRowIds[0]) {
            return refRowIds.map((record)=>String(record.id));
        }
        // Handle array of strings/numbers
        if (Array.isArray(refRowIds)) {
            return refRowIds.map((id)=>String(id));
        }
        // Handle single object with id property (lowercase - APIv3 format)
        if (typeof refRowIds === 'object' && refRowIds !== null && 'id' in refRowIds) {
            return [
                String(refRowIds.id)
            ];
        }
        // Handle single string/number
        return [
            String(refRowIds)
        ];
    }
    async nestedLink(context, param) {
        // Normalize the input to the expected format
        const normalizedRefRowIds = this.normalizeRefRowIds(param.refRowIds);
        this.dataTableService.validateIds(context, param.refRowIds);
        const { model, view } = await this.dataTableService.getModelAndView(context, param);
        const source = await _models.Source.get(context, model.source_id);
        const baseModel = await _models.Model.getBaseModelSQL(context, {
            id: model.id,
            viewId: view == null ? void 0 : view.id,
            dbDriver: await _NcConnectionMgrv2.default.get(source)
        });
        const column = await this.dataTableService.getColumn(context, param);
        await baseModel.model.getColumns(baseModel.context);
        await (0, _ltarcolsupdater.LTARColsUpdater)({
            baseModel,
            logger: this.logger
        }).updateLTARCol({
            linkDataPayload: {
                data: [
                    {
                        rowId: param.rowId,
                        links: Array.isArray(normalizedRefRowIds) ? normalizedRefRowIds : [
                            normalizedRefRowIds
                        ]
                    }
                ]
            },
            col: column,
            cookie: param.cookie,
            trx: baseModel.dbDriver
        });
        return {
            success: true
        };
    }
    async nestedUnlink(context, param) {
        // Normalize the input to the expected format
        const normalizedRefRowIds = this.normalizeRefRowIds(param.refRowIds);
        await this.dataTableService.nestedUnlink(context, {
            modelId: param.modelId,
            rowId: param.rowId,
            columnId: param.columnId,
            refRowIds: normalizedRefRowIds,
            query: param.query || {},
            cookie: param.cookie,
            viewId: param.viewId
        });
        return {
            success: true
        };
    }
    constructor(dataTableService){
        this.dataTableService = dataTableService;
        this.logger = new _common.Logger(DataV3Service.name);
    }
};
DataV3Service = _ts_decorate([
    (0, _common.Injectable)(),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof _datatableservice.DataTableService === "undefined" ? Object : _datatableservice.DataTableService
    ])
], DataV3Service);
